import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { LinkNode } from '@lexical/link'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  LineBreakNode,
  mergeRegister,
  RootNode,
  TextNode,
  type EditorState,
  type ElementNode,
  type LexicalEditor
} from 'lexical'
import React from 'react'
import { EDITOR_THEME } from './constants'
import { validateUrl } from './sanitize-url'
import { CharacterCounter } from './shared/CharacterCounter'
import { Toolbar } from './shared/Toolbar'
import type { TextEditorProps } from './text-editor.types'

function $importHtml(editor: LexicalEditor, html: string) {
  const dom = new window.DOMParser().parseFromString(html, 'text/html')
  const nodes = $generateNodesFromDOM(editor, dom)
  const root = $getRoot()
  root.clear()
  nodes.forEach((node) => {
    root.append($isElementNode(node) ? node : $createParagraphNode().append(node))
  })
  if (root.getChildrenSize() === 0) root.append($createParagraphNode())
}

// OnChangePlugin skips the initial HTML import, so sync the hidden inputs from the
// editor content once after mount to keep state and HTML from desyncing.
// https://github.com/facebook/lexical/blob/v0.45.0/packages/lexical-react/src/LexicalOnChangePlugin.ts

function InitialSyncPlugin({ onSync }: { onSync: (state: string, html: string) => void }) {
  const [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    const editorState = editor.getEditorState()
    editorState.read(
      () => {
        const hasText = $getRoot().getTextContent().trim().length > 0
        if (!hasText) return

        onSync(JSON.stringify(editorState.toJSON()), $generateHtmlFromNodes(editor, null))
      },
      { editor }
    )
  }, [editor, onSync])

  return null
}

function StripLinksPlugin() {
  const [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    return editor.registerNodeTransform(LinkNode, (node) => {
      node.getChildren().forEach((child) => node.insertBefore(child))
      node.remove()
    })
  }, [editor])

  return null
}

function StripBoldPlugin() {
  const [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    return editor.registerNodeTransform(TextNode, (node) => {
      if (node.hasFormat('bold')) node.toggleFormat('bold')
    })
  }, [editor])

  return null
}

function StripBlockTypesPlugin() {
  const [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    const demote = (node: ElementNode) => {
      node.replace($createParagraphNode().append(...node.getChildren()))
    }

    return mergeRegister(
      editor.registerNodeTransform(HeadingNode, demote),
      editor.registerNodeTransform(QuoteNode, demote)
    )
  }, [editor])

  return null
}

function SingleLinePlugin() {
  const [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          event?.preventDefault()
          return true
        },
        COMMAND_PRIORITY_HIGH
      ),

      editor.registerNodeTransform(LineBreakNode, (node) => {
        node.replace($createTextNode(' '))
      }),

      editor.registerNodeTransform(RootNode, (root) => {
        const [first, ...rest] = root.getChildren()
        if (rest.length === 0 || !$isElementNode(first)) return

        rest.forEach((node) => {
          if ($isElementNode(node)) first.append($createTextNode(' '), ...node.getChildren())
          node.remove()
        })
      })
    )
  }, [editor])

  return null
}

export const TextEditor = ({
  state,
  html,
  stateName,
  htmlName,
  maxLength,
  placeholder = 'Enter description…',
  toolbar,
  singleLine = false
}: TextEditorProps) => {
  const linksEnabled = toolbar?.link !== false
  const initialState =
    state == null ? undefined : typeof state === 'string' ? state : JSON.stringify(state)
  const [stateValue, setStateValue] = React.useState(initialState ?? '')
  const [htmlValue, setHtmlValue] = React.useState(html ?? '')
  const initialConfig = {
    namespace: 'text-editor',
    theme: EDITOR_THEME,
    nodes: [HeadingNode, QuoteNode, LinkNode],
    editorState:
      initialState ?? (html ? (editor: LexicalEditor) => $importHtml(editor, html) : undefined),
    onError: (error: Error) => {
      throw error
    }
  }

  const handleChange = React.useCallback((editorState: EditorState, editor: LexicalEditor) => {
    setStateValue(JSON.stringify(editorState.toJSON()))
    editorState.read(
      () => {
        const hasText = $getRoot().getTextContent().trim().length > 0
        setHtmlValue(hasText ? $generateHtmlFromNodes(editor, null) : '')
      },
      { editor }
    )
  }, [])

  const handleInitialSync = React.useCallback((state: string, html: string) => {
    setStateValue(state)
    setHtmlValue(html)
  }, [])

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="overflow-hidden rounded-lg border border-border">
        <Toolbar options={toolbar} />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="h-36 overflow-y-auto p-3 focus:outline-none"
                aria-placeholder={placeholder}
                placeholder={
                  <div className="text-black/25 absolute top-5 left-3 select-none pointer-events-none">
                    {placeholder}
                  </div>
                }
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        {toolbar?.bold === false && <StripBoldPlugin />}
        {toolbar?.blockType === false && <StripBlockTypesPlugin />}
        {singleLine && <SingleLinePlugin />}
        <HistoryPlugin />
        {linksEnabled ? (
          <LinkPlugin
            validateUrl={validateUrl}
            attributes={{ rel: 'noopener noreferrer nofollow', target: '_blank' }}
          />
        ) : (
          <StripLinksPlugin />
        )}
        <OnChangePlugin onChange={handleChange} />
        <InitialSyncPlugin onSync={handleInitialSync} />
      </div>

      <CharacterCounter maxLength={maxLength} />

      <input type="hidden" name={stateName} value={stateValue} />
      <input type="hidden" name={htmlName} value={htmlValue} />
    </LexicalComposer>
  )
}
